/**
 * Создание проекта.
 *
 * Обязательно только название: остальное заполняется позже. Форма не должна
 * превращаться в барьер (документ «Структура интерфейса», §6).
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import type { WorkspaceDto } from '@project-os/contracts';
import { createProjectSchema } from '@project-os/validation';
import { ApiError } from '@/api/client';
import { useCreateProject } from '@/api/projects';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

export function CreateProjectDialog({
  open,
  onOpenChange,
  workspaces,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaces: WorkspaceDto[];
}) {
  const navigate = useNavigate();
  const createProject = useCreateProject();

  const writableWorkspaces = workspaces.filter((workspace) => workspace.role !== 'VIEWER');
  const defaultWorkspaceId = writableWorkspaces[0]?.id ?? '';

  const form = useForm({
    resolver: zodResolver(createProjectSchema),
    defaultValues: {
      workspaceId: defaultWorkspaceId,
      name: '',
      descriptionMd: '',
      status: 'ACTIVE',
      currentGoal: '',
      startDate: null,
      targetDate: null,
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        workspaceId: defaultWorkspaceId,
        name: '',
        descriptionMd: '',
        status: 'ACTIVE',
        currentGoal: '',
        startDate: null,
        targetDate: null,
      });
    }
    // Сброс нужен при каждом открытии, значение по умолчанию может измениться.
  }, [open, defaultWorkspaceId, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const project = await createProject.mutateAsync({
        ...values,
        descriptionMd: values.descriptionMd || null,
        currentGoal: values.currentGoal || null,
      });
      toast.success('Проект создан');
      onOpenChange(false);
      void navigate(`/projects/${project.id}`);
    } catch (error) {
      if (error instanceof ApiError && error.fields) {
        for (const [field, message] of Object.entries(error.fields)) {
          form.setError(field as Parameters<typeof form.setError>[0], { message });
        }
      }
    }
  });

  const formError =
    createProject.error instanceof ApiError && !createProject.error.fields
      ? createProject.error
      : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Новый проект</DialogTitle>
          <DialogDescription>
            Достаточно названия. Направления, этапы и участники добавляются после создания.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            {formError ? (
              <Alert variant="destructive">
                <AlertDescription>{formError.message}</AlertDescription>
              </Alert>
            ) : null}

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Название</FormLabel>
                  <FormControl>
                    <Input autoFocus placeholder="Например: Сервис аренды в Берлине" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              {writableWorkspaces.length > 1 ? (
                <FormField
                  control={form.control}
                  name="workspaceId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Рабочее пространство</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {writableWorkspaces.map((workspace) => (
                            <SelectItem key={workspace.id} value={workspace.id}>
                              {workspace.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Состояние</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="IDEA">Идея</SelectItem>
                        <SelectItem value="ACTIVE">В работе</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="currentGoal"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Текущая цель</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={2}
                      placeholder="Чего команда должна добиться прямо сейчас"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormDescription>Необязательно. Можно задать позже.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Дата начала</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="targetDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Целевая дата</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Отмена
              </Button>
              <Button type="submit" disabled={createProject.isPending}>
                {createProject.isPending ? <Loader2Icon className="animate-spin" /> : null}
                Создать проект
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
